import { useState } from 'react';
import { Alert, Card, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT_PAGE_SIZE,
  itemsOf,
  listDocuments,
  type DocumentState,
  type PAList,
  type PADocument,
} from '../api/money';
import { downloadFile } from '../api/download';
import { currentPeriod, formatInstantDay, shiftPeriod } from '../format';
import { Money } from '../money/Money';
import { DataTable, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { MonthSpinner } from '../money/MonthSpinner';
import { Pager } from '../money/Pager';
import { usePAQuery } from '../money/usePAQuery';
import { errorText } from '../session/errorText';
import { documentStateLabel } from '../money/labels';

/**
 * «Ежемесячный счёт» — Appendix L §19: the documents the operator lets a subscriber see, and
 * the one route to their bytes.
 *
 * ============================================================================
 * ⚠ THE VISIBILITY DECISION IS THE SERVER'S, ON BOTH DOORS — D211
 * ============================================================================
 *
 * `document_template.available_in_personal_area` had existed since migration 0113 with NO
 * consumer anywhere: it was scanned, saved, edited on the admin screen, and no WHERE clause
 * ever read it, so switching portal visibility off changed nothing at all. W2's backend is its
 * first consumer, and BOTH doors carry the predicate — the list filters on it AND
 * `GET /pa/documents/{id}/file` re-checks it on the individual document, through one shared
 * function so the two cannot drift.
 *
 * **This screen therefore never decides what a subscriber may see, and never builds a
 * download URL.** It renders exactly the rows the server returned, and the «Скачать» link is
 * `download_url` AS GIVEN. Constructing `/pa/documents/${id}/file` here would be a client
 * deciding which ids are reachable — which is the enumeration the flat 404 exists to prevent —
 * and it would let a future list bug (a row rendered from stale state, a filter applied on the
 * client) offer bytes the server would refuse. Following the server's own link keeps the set of
 * reachable documents exactly the set it offered.
 *
 * The link is a REAL ANCHOR carrying the server's own `download_url`: the session is an
 * HttpOnly cookie, the route is same-origin, and the server answers with
 * `Content-Disposition: attachment`. «Copy link», «open in new tab» and a long-press save all
 * keep working on the native path.
 *
 * ~~It is a plain anchor and not a `fetch`: a `fetch` + `URL.createObjectURL` version would
 * need a blob in memory and would break the one thing that must work on a phone — «Save to
 * Files».~~ *(Amended 2026-08-12, W1+W2 fix pass.)* The trade-off was real and the conclusion
 * was too strong: a plain anchor hands the route's THREE REFUSALS to the browser, which says
 * «Failed» or replaces the portal with a raw JSON envelope, so `503 pa.storage_unavailable` —
 * «the document is yours and we cannot produce it», the answer an installation with no blob
 * storage gives for EVERY document while the list renders perfectly — never reached the
 * subscriber at all. The click is now handled (`src/api/download.ts`), the failure goes
 * through `errorText` like every other failure in this app, and the blob path FALLS BACK to
 * navigating the same URL when `URL.createObjectURL` is unavailable, so a browser that
 * dislikes blob downloads degrades to the old behaviour rather than to a broken button. The
 * mobile claim above was never measured and is not measured here either — said plainly.
 *
 * # The two «column shown only if enabled» toggles of §19 are NOT built
 *
 * State and Download are shown unconditionally. Each toggle would be an operator setting with
 * no editor anywhere — a control nobody can reach — and inventing three settings to hide two
 * columns is worse than showing them. Recorded here so it reads as a decision rather than an
 * oversight; whoever wants them adds the settings AND their editor together.
 */
export function DocumentsScreen() {
  return (
    <MoneyPage titleKey="documents.title" leadKey="documents.lead">
      {(contract) => <DocumentsBody contractID={contract.id} />}
    </MoneyPage>
  );
}

function DocumentsBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  // §19's default window: the current month and the eleven before it. A subscriber opening
  // «Ежемесячный счёт» wants this year's invoices, and an unbounded default would page a large
  // estate's whole history onto a handset.
  const [from, setFrom] = useState(() => shiftPeriod(currentPeriod(), -11));
  const [to, setTo] = useState(() => currentPeriod());
  const [state, setState] = useState<DocumentState>('');
  const [offset, setOffset] = useState(0);
  // The one thing on this screen that is neither a read nor a render: what went wrong when
  // he pressed «Скачать». See [DownloadLink].
  const [failure, setFailure] = useState<unknown>(null);

  const { query, reload } = usePAQuery<PAList<PADocument>>(
    () => listDocuments(contractID, { from, to, state, offset }),
    [contractID, from, to, state, offset],
  );

  const columns: ReadonlyArray<Column<PADocument>> = [
    {
      key: 'date',
      labelKey: 'documents.columnDate',
      render: (d) => formatInstantDay(d.issue_date),
    },
    {
      key: 'number',
      labelKey: 'documents.columnNumber',
      render: (d) => <span className="mono">{d.number}</span>,
    },
    {
      key: 'kind',
      labelKey: 'documents.columnKind',
      track: 'minmax(0, 1.6fr)',
      render: (d) => d.template_name,
    },
    {
      key: 'amount',
      labelKey: 'documents.columnAmount',
      className: 'pa-td-right',
      render: (d) => (
        <>
          <Money minor={d.amount} currency={d.currency} />
          {/* ⚠ `vat_amount` WAS ON THE WIRE AND RENDERED NOWHERE. A legal entity reading
              his own invoice in the cabinet could not see the tax the server had already
              sent him — and on this installation the rate is per OPERATOR (5 % / 22 %),
              so it is not a figure he can reconstruct from the total. Zero is omitted
              rather than printed: «в том числе НДС 0,00» on a non-VAT document is noise. */}
          {d.vat_amount ? (
            <span className="pa-cell-sub">
              {t('documents.vat')} <Money minor={d.vat_amount} currency={d.currency} />
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: 'state',
      labelKey: 'documents.columnState',
      // BOTH fields, because there are three facts: paid, owed, and nothing to pay. Keying
      // on `paid_at` alone rendered an act — never payable — exactly like an unpaid invoice.
      render: (d) => (
        <span className="pa-status">{documentStateLabel(d.payable, d.paid_at, t)}</span>
      ),
    },
    {
      key: 'action',
      labelKey: 'documents.columnAction',
      render: (d) => <DownloadLink doc={d} onFailure={setFailure} />,
    },
  ];

  return (
    <>
      <Card className="pa-block pa-filters">
        <MonthSpinner
          labelKey="documents.fromLabel"
          period={from}
          onChange={(next) => {
            setOffset(0);
            setFrom(next);
          }}
        />
        <MonthSpinner
          labelKey="documents.toLabel"
          period={to}
          onChange={(next) => {
            setOffset(0);
            setTo(next);
          }}
        />
        <div className="pa-picker">
          <span className="pa-picker-label">{t('documents.stateLabel')}</span>
          <Radio.Group
            value={state}
            onChange={(e) => {
              setOffset(0);
              setState(e.target.value as DocumentState);
            }}
            options={[
              { value: '', label: t('documents.stateAll') },
              { value: 'unpaid', label: t('documents.stateUnpaid') },
              { value: 'paid', label: t('documents.statePaid') },
            ]}
          />
        </div>
      </Card>

      {failure ? (
        <Alert
          className="pa-block"
          type="error"
          showIcon
          closable
          onClose={() => setFailure(null)}
          message={t('documents.downloadFailed')}
          description={errorText(failure, t)}
        />
      ) : null}

      <QueryFrame query={query} reload={reload}>
        {(list) => (
          <>
            <DataTable
              columns={columns}
              rows={itemsOf(list)}
              rowKey={(d) => d.id}
              emptyKey="documents.empty"
            />
            <Pager
              offset={offset}
              pageSize={DOCUMENT_PAGE_SIZE}
              shown={itemsOf(list).length}
              total={list.total}
              onOffset={setOffset}
            />
          </>
        )}
      </QueryFrame>
    </>
  );
}

/**
 * «Скачать» — a REAL ANCHOR whose failure the subscriber can read.
 *
 * ⚠ THE `href` IS THE SERVER'S OWN `download_url`, UNCHANGED. Nothing here builds a
 * document path: that would be a client deciding which ids are reachable, which is the
 * enumeration the flat 404 exists to prevent, and `DocumentsScreen.test.tsx`'s source sweep
 * fails on any component that so much as names `/pa/documents`. Keeping a real `href` also
 * keeps «copy link», «open in new tab» and a long-press save on the NATIVE path.
 *
 * What the click handler adds is the outcome. This route refuses in three ways the
 * subscriber is entitled to distinguish — the row is not his (404), the session slid past
 * its TTL (401), and THE DOCUMENT IS HIS AND WE CANNOT PRODUCE IT (503
 * `pa.storage_unavailable`, which is what an installation with no blob storage answers for
 * EVERY document while the list renders perfectly) — and a plain anchor hands all three to
 * the browser, which says «Failed» or replaces the page with a raw JSON envelope. Neither
 * reaches `errorText`, the one place a failure becomes a sentence in this app.
 *
 * The busy label is a bound expression, never a literal `disabled`: an anchor that has been
 * pressed and is fetching says so, and one that has not is an ordinary link.
 */
function DownloadLink({
  doc,
  onFailure,
}: {
  doc: PADocument;
  onFailure: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  return (
    <a
      className="pa-download"
      href={doc.download_url}
      download
      onClick={(e) => {
        // ⚠ A MODIFIED CLICK IS THE BROWSER'S, NOT OURS. Ctrl/⌘/Shift-click and a
        // middle-click mean «open this somewhere else», and they still fire `onClick`;
        // calling `preventDefault` on them would take «open in a new tab» away from a
        // subscriber to add an error message he did not need. The href is real, so
        // letting the event through costs nothing.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onFailure(null);
        setBusy(true);
        void downloadFile(doc.download_url, doc.filename)
          .catch(onFailure)
          .finally(() => setBusy(false));
      }}
    >
      {busy ? t('documents.downloading') : t('documents.download')}
    </a>
  );
}
